import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  FileText,
  Loader2,
  Paperclip,
  ScanSearch,
  Upload,
  X,
} from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { Chip } from '../interviews/Chip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../lib/shadcn/alert-dialog'
import {
  useGetDisputeMatter,
  useReadDisputeDocument,
  useConfirmDisputeMatter,
  useSaveDisputeEvent,
  useSupersedeDisputeEvent,
  useRecordDisputeEventOutcome,
  useDecideDisputeRecommendation,
  useAddDisputeRecommendation,
  useCloseDisputeMatter,
  useWithdrawDuplicateMatter,
  useGetDisputeDocumentUrl,
  useGetDisputeDriverCandidates,
} from '../../hooks/backend/disputes'
import { useDisputeUpload, MAX_READABLE_BYTES } from '../../hooks/useDisputeUpload'
import { writeErrorMessage } from '../../utils/writeError'
import {
  CLOSE_STATUSES,
  canWithdrawAsDuplicate,
  DOC_KINDS,
  EVENT_TYPES,
  eventStateLabel,
  eventTone,
  formatDate,
  formatWhen,
  type DisputeEvent,
  type DisputeMatter,
} from '../../utils/disputes'

type Detail = {
  matter: DisputeMatter
  model_reading: Record<string, unknown> | null
  documents: Array<{
    id: string
    filename: string
    mime_type: string | null
    size_bytes: number | null
    doc_kind: string
    uploaded_at: string
    uploaded_by: string
  }>
  events: DisputeEvent[]
  recommendations: Array<{
    id: string
    seq: number
    action: string
    rationale: string | null
    by_when: string | null
    by_when_source: string | null
    source: string
    status: string
    decided_by: string | null
    decline_reason: string | null
    task_ref: number | null
  }>
}

function Section({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-background p-3">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {right ? <div className="ml-auto">{right}</div> : null}
      </div>
      {children}
    </section>
  )
}

/**
 * One matter, and everything a person has to do to it.
 *
 * THE PANEL IS ORDERED BY WHAT IT REFUSES TO ASSUME, top to bottom: the paper,
 * then what a machine read off it, then what a person confirms, then the diary,
 * then the work. Nothing lower down binds anything until the step above it has
 * been done by a person, and the screen says which step that is rather than
 * greying a button and leaving somebody to guess.
 */
export function MatterDetail({
  matterId,
  canManage,
  onChanged,
  onClose,
}: {
  matterId: string
  canManage: boolean
  onChanged: () => void
  onClose: () => void
}) {
  const detail = useGetDisputeMatter<Detail>()
  const detailTrigger = detail.trigger

  const read = useReadDisputeDocument()
  const confirm = useConfirmDisputeMatter()
  const saveEvent = useSaveDisputeEvent()
  const supersede = useSupersedeDisputeEvent()
  const outcome = useRecordDisputeEventOutcome()
  const decide = useDecideDisputeRecommendation()
  const addRec = useAddDisputeRecommendation()
  const closeMatter = useCloseDisputeMatter()
  const withdrawDup = useWithdrawDuplicateMatter()
  const docUrl = useGetDisputeDocumentUrl()
  const candidates = useGetDisputeDriverCandidates<
    Array<{ driver_id: string; full_name: string; phone: string | null; status: string }>
  >()
  const { upload } = useDisputeUpload()

  const fileRef = useRef<HTMLInputElement | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [docKind, setDocKind] = useState<string>('referral')

  const load = useCallback(() => {
    detailTrigger({ id: matterId }, { skipCache: true })
  }, [detailTrigger, matterId])

  useEffect(() => {
    load()
  }, [load])

  const m = detail.data?.matter ?? null
  const events = detail.data?.events ?? []
  const recs = detail.data?.recommendations ?? []
  const docs = detail.data?.documents ?? []
  const reading = detail.data?.model_reading as Record<string, unknown> | null

  // The confirm form. Seeded from whatever is on the matter, which after a
  // reading is the model's proposal. The person edits it and presses Confirm,
  // and that press is the whole difference between a reading and a record.
  const [caseNumber, setCaseNumber] = useState('')
  const [matterType, setMatterType] = useState('')
  const [applicant, setApplicant] = useState('')
  const [driverId, setDriverId] = useState('')
  const [driverLink, setDriverLink] = useState<'confirmed' | 'no driver record' | 'unlinked'>('unlinked')
  const [linkNote, setLinkNote] = useState('')

  useEffect(() => {
    if (!m) return
    setCaseNumber(m.case_number ?? '')
    setMatterType(m.matter_type ?? '')
    setApplicant(m.applicant_name ?? '')
    setDriverId(m.driver_id ?? '')
    setDriverLink(
      m.driver_link === 'confirmed' || m.driver_link === 'no driver record' ? m.driver_link : 'unlinked',
    )
    setLinkNote(m.driver_link_note ?? '')
  }, [m])

  const candidateTrigger = candidates.trigger
  useEffect(() => {
    const name = (m?.applicant_name ?? '').trim()
    if (name) candidateTrigger({ name })
  }, [candidateTrigger, m?.applicant_name])

  const warnings = useMemo(() => {
    const w = reading?.warnings
    return Array.isArray(w) ? (w as string[]) : []
  }, [reading])

  const run = useCallback(
    async (key: string, fn: () => PromiseLike<unknown>, done: string) => {
      setBusy(key)
      try {
        await fn()
        toast.success(done)
        load()
        onChanged()
      } catch (err) {
        toast.error(writeErrorMessage(err))
      } finally {
        setBusy(null)
      }
    },
    [load, onChanged],
  )

  const onFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return
      setBusy('upload')
      try {
        const out = await upload(Array.from(files), matterId, docKind)
        if (out.done > 0) toast.success(`${out.done} document${out.done === 1 ? '' : 's'} filed.`)
        for (const f of out.failed) toast.error(f)
        for (const f of out.tooBig) toast.error(`${f} is over 20 MB and was not uploaded.`)
        if (out.tooBigToRead.length > 0) {
          toast.warning(
            `${out.tooBigToRead.join(', ')} ${out.tooBigToRead.length === 1 ? 'is' : 'are'} stored but too large to be read automatically. Read ${out.tooBigToRead.length === 1 ? 'it' : 'them'} yourself.`,
          )
        }
        load()
        onChanged()
      } finally {
        setBusy(null)
        if (fileRef.current) fileRef.current.value = ''
      }
    },
    [upload, matterId, docKind, load, onChanged],
  )

  const openDoc = useCallback(
    async (id: string) => {
      try {
        const res = (await docUrl.trigger({ id }, { skipCache: true })) as { url?: string } | null
        if (res?.url) window.open(res.url, '_blank', 'noopener')
      } catch (err) {
        toast.error(writeErrorMessage(err))
      }
    },
    [docUrl],
  )

  if (detail.loading && !m) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Opening the matter…
      </div>
    )
  }
  if (!m) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        {detail.error ?? 'That matter is no longer on record.'}
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-start gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold text-foreground">
            {m.forum} matter #{m.ref}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Received {formatDate(m.received_at)} by {m.received_by}
            {m.task_ref ? `. Task #${m.task_ref}${m.task_status ? `, ${m.task_status.toLowerCase()}` : ''}.` : '.'}
          </p>
        </div>
        <Button variant="ghost" size="icon" className="ml-auto shrink-0" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        {/* 1. The paper */}
        <Section
          title="The document"
          right={
            <div className="flex items-center gap-2">
              <NativeSelect
                className="h-8 w-44 text-xs"
                value={docKind}
                onChange={(e) => setDocKind(e.target.value)}
                aria-label="What kind of document"
              >
                {DOC_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </NativeSelect>
              <input
                ref={fileRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => void onFiles(e.target.files)}
              />
              <Button
                size="sm"
                variant="outline"
                disabled={busy !== null}
                onClick={() => fileRef.current?.click()}
              >
                {busy === 'upload' ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="mr-1.5 h-3.5 w-3.5" />
                )}
                Upload
              </Button>
            </div>
          }
        >
          {docs.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Nothing filed yet. Upload the letter, then have it read.
            </p>
          ) : (
            <ul className="space-y-1">
              {docs.map((d) => (
                <li key={d.id} className="flex items-center gap-2 text-xs">
                  <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <button
                    type="button"
                    className="truncate text-left font-medium text-foreground underline-offset-2 hover:underline"
                    onClick={() => void openDoc(d.id)}
                  >
                    {d.filename}
                  </button>
                  <Chip tone="neutral">{d.doc_kind}</Chip>
                  {d.size_bytes !== null && d.size_bytes > MAX_READABLE_BYTES && (
                    <Chip tone="amber">too large to read automatically</Chip>
                  )}
                  <span className="ml-auto shrink-0 text-muted-foreground">
                    {formatDate(d.uploaded_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* 2. What a machine read off it */}
        <Section
          title="What the reading says"
          right={
            <Button
              size="sm"
              variant="outline"
              disabled={busy !== null || docs.length === 0 || !m.is_open}
              onClick={() =>
                void run('read', () => read.trigger({ id: matterId }, { skipCache: true }), 'Read.')
              }
            >
              {busy === 'read' ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <ScanSearch className="mr-1.5 h-3.5 w-3.5" />
              )}
              {m.model_read_at ? 'Read again' : 'Read the document'}
            </Button>
          }
        >
          {m.read_error && (
            <p className="mb-2 rounded-md bg-amber-100 p-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
              <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
              {m.read_error}
            </p>
          )}
          {!m.model_read_at && !m.read_error && (
            <p className="text-xs text-muted-foreground">
              Not read yet. Nothing here is required: a person can confirm the case number and the
              date straight off the paper and skip this entirely.
            </p>
          )}
          {m.model_read_at && (
            <div className="space-y-2 text-xs">
              <p className="text-muted-foreground">
                Read {formatDate(m.model_read_at)}. Every date below landed unconfirmed and nothing
                here has been acted on.
              </p>
              {typeof reading?.what_is_claimed === 'string' && (
                <p className="text-foreground">{reading.what_is_claimed as string}</p>
              )}
              {Array.isArray(reading?.what_the_document_requires) &&
                (reading?.what_the_document_requires as string[]).length > 0 && (
                  <div>
                    <p className="font-medium text-foreground">What the document says we must do</p>
                    <ul className="ml-4 list-disc text-muted-foreground">
                      {(reading?.what_the_document_requires as string[]).map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  </div>
                )}
              {warnings.length > 0 && (
                <div>
                  <p className="font-medium text-amber-700 dark:text-amber-300">
                    What it could not read
                  </p>
                  <ul className="ml-4 list-disc text-amber-700 dark:text-amber-300">
                    {warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Section>

        {/* 3. What a person confirms */}
        <Section title="Who this is about">
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Case number</Label>
              <Input
                className="h-8 text-sm"
                value={caseNumber}
                onChange={(e) => setCaseNumber(e.target.value)}
                placeholder="not on the document"
              />
            </div>
            <div>
              <Label className="text-xs">What kind of matter</Label>
              <Input
                className="h-8 text-sm"
                value={matterType}
                onChange={(e) => setMatterType(e.target.value)}
                placeholder="Unfair dismissal, unpaid wages, and so on"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Applicant, as the document writes it</Label>
              <Input
                className="h-8 text-sm"
                value={applicant}
                onChange={(e) => setApplicant(e.target.value)}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">The driver</Label>
              <NativeSelect
                className="h-8 text-sm"
                value={driverLink === 'confirmed' ? driverId : driverLink}
                onChange={(e) => {
                  const v = e.target.value
                  if (v === 'no driver record' || v === 'unlinked') {
                    setDriverLink(v)
                    setDriverId('')
                  } else {
                    setDriverLink('confirmed')
                    setDriverId(v)
                  }
                }}
              >
                <option value="unlinked">Not decided yet</option>
                <option value="no driver record">
                  No driver record: he is not on our books
                </option>
                {(candidates.data ?? []).map((c) => (
                  <option key={c.driver_id} value={c.driver_id}>
                    {c.full_name}
                    {c.status !== 'active' ? ` (${c.status})` : ''}
                  </option>
                ))}
              </NativeSelect>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {(candidates.data ?? []).length === 0
                  ? 'No driver on file shares a name with the applicant. That is normal for somebody who left before this system, and "no driver record" is a real answer.'
                  : 'Names that share a word with the applicant, active and archived. Pick the man, or say there is no record. Nothing is guessed for you.'}
              </p>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Note (optional)</Label>
              <Input
                className="h-8 text-sm"
                value={linkNote}
                onChange={(e) => setLinkNote(e.target.value)}
                placeholder="anything a reader would need to understand the link"
              />
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Button
              size="sm"
              variant="brand"
              disabled={busy !== null || !canManage || driverLink === 'unlinked'}
              onClick={() =>
                void run(
                  'confirm',
                  () =>
                    confirm.trigger(
                      {
                        id: matterId,
                        case_number: caseNumber || null,
                        matter_type: matterType || null,
                        applicant_name: applicant || null,
                        driver_id: driverLink === 'confirmed' ? driverId : null,
                        driver_link: driverLink,
                        note: linkNote || null,
                      },
                      { skipCache: true },
                    ),
                  'Confirmed.',
                )
              }
            >
              {busy === 'confirm' ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
              )}
              Confirm
            </Button>
            {!canManage && (
              <span className="text-[11px] text-muted-foreground">
                Confirming a matter needs the disputes capability. Ask an admin if it should be yours.
              </span>
            )}
            {canManage && driverLink === 'unlinked' && (
              <span className="text-[11px] text-muted-foreground">
                Pick the driver, or record that there is no driver record. The matter will not be
                filed against a guess.
              </span>
            )}
          </div>
        </Section>

        {/* 4. The diary */}
        <Section title="Dates">
          {events.length === 0 ? (
            <p className="text-xs text-muted-foreground">No date on file.</p>
          ) : (
            <ul className="space-y-2">
              {events.map((e) => (
                <li key={e.id} className="rounded-md border border-border p-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <CalendarClock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">{e.event_type}</span>
                    <span className="text-sm text-foreground">{formatWhen(e)}</span>
                    <Chip tone={eventTone(e)}>{eventStateLabel(e)}</Chip>
                    {e.source === 'model' && !e.is_confirmed && <Chip tone="neutral">read by the model</Chip>}
                    {e.calendar_error && <Chip tone="red">calendar: {e.calendar_error}</Chip>}
                    {e.calendar_synced_at && <Chip tone="green">in the calendar</Chip>}
                  </div>
                  {e.venue && <p className="mt-1 text-xs text-muted-foreground">{e.venue}</p>}
                  {e.outcome && (
                    <p className="mt-1 text-xs text-foreground">Outcome: {e.outcome}</p>
                  )}
                  {canManage && !e.outcome && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {!e.is_confirmed && (
                        <Button
                          size="sm"
                          variant="brand"
                          disabled={busy !== null}
                          onClick={() =>
                            void run(
                              `confirm-${e.id}`,
                              () =>
                                saveEvent.trigger(
                                  {
                                    matter_id: matterId,
                                    event_id: e.id,
                                    event_type: e.event_type,
                                    scheduled_on: e.scheduled_on,
                                    at_time: e.scheduled_at_time,
                                    venue: e.venue,
                                    confirm: true,
                                  },
                                  { skipCache: true },
                                ),
                              'Date confirmed. It is now in the diary and on the board.',
                            )
                          }
                        >
                          I have read this off the document. Confirm it
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy !== null}
                        onClick={() => {
                          const outcomeText = window.prompt('What happened on the day?')
                          if (!outcomeText) return
                          void run(
                            `outcome-${e.id}`,
                            () =>
                              outcome.trigger(
                                { event_id: e.id, outcome: outcomeText },
                                { skipCache: true },
                              ),
                            'Outcome recorded.',
                          )
                        }}
                      >
                        Record the outcome
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy !== null}
                        onClick={() => {
                          const reason = window.prompt('Why did this date fall away?')
                          if (!reason) return
                          void run(
                            `supersede-${e.id}`,
                            () => supersede.trigger({ event_id: e.id, reason }, { skipCache: true }),
                            'Recorded as superseded. Add the new date beside it.',
                          )
                        }}
                      >
                        Postponed
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {canManage && m.is_open && <AddEventRow matterId={matterId} busy={busy} run={run} saveEvent={saveEvent} />}
        </Section>

        {/* 5. The work */}
        <Section title="Recommendations">
          {recs.length === 0 ? (
            <p className="text-xs text-muted-foreground">Nothing proposed yet.</p>
          ) : (
            <ul className="space-y-2">
              {recs.map((r) => (
                <li key={r.id} className="rounded-md border border-border p-2 text-xs">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-foreground">{r.action}</span>
                    <Chip
                      tone={r.status === 'accepted' ? 'green' : r.status === 'declined' ? 'neutral' : 'amber'}
                    >
                      {r.status}
                    </Chip>
                    {r.source === 'model' && <Chip tone="neutral">proposed by the model</Chip>}
                    {r.by_when && (
                      <Chip tone="neutral">
                        by {formatDate(r.by_when)}
                        {r.by_when_source === 'document' ? ', from the document' : ''}
                      </Chip>
                    )}
                    {r.task_ref && <Chip tone="brand">task #{r.task_ref}</Chip>}
                  </div>
                  {r.rationale && <p className="mt-1 text-muted-foreground">{r.rationale}</p>}
                  {r.decline_reason && (
                    <p className="mt-1 text-muted-foreground">Declined: {r.decline_reason}</p>
                  )}
                  {canManage && r.status === 'proposed' && (
                    <div className="mt-2 flex gap-2">
                      <Button
                        size="sm"
                        variant="brand"
                        disabled={busy !== null}
                        onClick={() => {
                          const by = window.prompt(
                            'Done by which date? Leave blank for no date. Fleetly does not compute statutory time limits, so this is your date.',
                            r.by_when ?? '',
                          )
                          if (by === null) return
                          void run(
                            `rec-${r.id}`,
                            () =>
                              decide.trigger(
                                { id: r.id, decision: 'accepted', by_when: by || null },
                                { skipCache: true },
                              ),
                            'Accepted. It is a task on the board.',
                          )
                        }}
                      >
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy !== null}
                        onClick={() => {
                          const reason = window.prompt('Why is this not being done?')
                          if (!reason) return
                          void run(
                            `rec-${r.id}`,
                            () =>
                              decide.trigger(
                                { id: r.id, decision: 'declined', reason },
                                { skipCache: true },
                              ),
                            'Declined, with the reason on the record.',
                          )
                        }}
                      >
                        Decline
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {canManage && m.is_open && (
            <div className="mt-2">
              <Button
                size="sm"
                variant="outline"
                disabled={busy !== null}
                onClick={() => {
                  const action = window.prompt('What should be done?')
                  if (!action) return
                  void run(
                    'add-rec',
                    () => addRec.trigger({ matter_id: matterId, action }, { skipCache: true }),
                    'Added.',
                  )
                }}
              >
                <Paperclip className="mr-1.5 h-3.5 w-3.5" />
                Add one of your own
              </Button>
            </div>
          )}
        </Section>

        {canManage && m.is_open && <CloseMatterRow matterId={matterId} busy={busy} run={run} closeMatter={closeMatter} />}
        {canWithdrawAsDuplicate(m, canManage) && (
          <WithdrawDuplicateRow
            matterId={matterId}
            matterRef={m.ref}
            busy={busy}
            run={run}
            withdraw={withdrawDup}
          />
        )}
        {!m.is_open && (
          <Section title="Closed">
            <p className="text-xs text-foreground">
              {m.status}. {m.outcome ?? ''}
            </p>
          </Section>
        )}
      </div>
    </div>
  )
}

type Runner = (key: string, fn: () => PromiseLike<unknown>, done: string) => Promise<void>

// The generated hooks return a trigger whose params are `unknown` and whose
// result is a TriggerResult, not a bare Promise. Named here rather than
// hand-written in each prop list, because writing it out by hand is what produced
// a type error that looked like a bug in the hook.
type Triggerable = {
  trigger: (params?: unknown, options?: { skipCache?: boolean }) => PromiseLike<unknown>
}

function AddEventRow({
  matterId,
  busy,
  run,
  saveEvent,
}: {
  matterId: string
  busy: string | null
  run: Runner
  saveEvent: Triggerable
}) {
  const [type, setType] = useState<string>('Conciliation')
  const [on, setOn] = useState('')
  const [at, setAt] = useState('')
  const [venue, setVenue] = useState('')

  return (
    <div className="mt-3 grid gap-2 border-t border-border pt-3 sm:grid-cols-4">
      <NativeSelect className="h-8 text-xs" value={type} onChange={(e) => setType(e.target.value)}>
        {EVENT_TYPES.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </NativeSelect>
      <Input className="h-8 text-xs" type="date" value={on} onChange={(e) => setOn(e.target.value)} />
      <Input
        className="h-8 text-xs"
        type="time"
        value={at}
        onChange={(e) => setAt(e.target.value)}
        placeholder="leave blank if the document gives none"
      />
      <Input
        className="h-8 text-xs"
        value={venue}
        onChange={(e) => setVenue(e.target.value)}
        placeholder="venue"
      />
      <div className="sm:col-span-4">
        <Button
          size="sm"
          variant="outline"
          disabled={busy !== null || !on}
          onClick={() =>
            void run(
              'add-event',
              () =>
                saveEvent.trigger(
                  {
                    matter_id: matterId,
                    event_type: type,
                    scheduled_on: on,
                    at_time: at || null,
                    venue: venue || null,
                    confirm: true,
                  },
                  { skipCache: true },
                ),
              'Date added and confirmed.',
            )
          }
        >
          Add a date from the document
        </Button>
        <span className="ml-2 text-[11px] text-muted-foreground">
          Leave the time blank if the document does not give one. It will show as "time not stated"
          rather than an hour nobody wrote down.
        </span>
      </div>
    </div>
  )
}

function CloseMatterRow({
  matterId,
  busy,
  run,
  closeMatter,
}: {
  matterId: string
  busy: string | null
  run: Runner
  closeMatter: Triggerable
}) {
  const [status, setStatus] = useState<string>('Settled')
  const [text, setText] = useState('')

  return (
    <Section title="Close the matter">
      <div className="grid gap-2 sm:grid-cols-3">
        <NativeSelect
          className="h-8 text-xs"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {CLOSE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </NativeSelect>
        <Textarea
          className="sm:col-span-2"
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What was the outcome? This is the only thing anybody will ask about this matter in a year."
        />
      </div>
      <Button
        className="mt-2"
        size="sm"
        variant="outline"
        disabled={busy !== null || !text.trim()}
        onClick={() =>
          void run(
            'close',
            () =>
              closeMatter.trigger(
                { id: matterId, status, outcome: text.trim() },
                { skipCache: true },
              ),
            'Closed.',
          )
        }
      >
        Close
      </Button>
    </Section>
  )
}

/**
 * Withdraw a matter that was filed twice. Platform request #7.
 *
 * Not a delete and not "Withdrawn": the matter stays on record as a Duplicate
 * pointing at the one it repeats, leaves the board and the diary, and keeps its
 * documents. The confirmation says exactly that before anything is written,
 * because the person pressing it is usually the one who uploaded by mistake and
 * the obvious fear is losing the paper.
 */
function WithdrawDuplicateRow({
  matterId,
  matterRef,
  busy,
  run,
  withdraw,
}: {
  matterId: string
  matterRef: number
  busy: string | null
  run: Runner
  withdraw: Triggerable
}) {
  const [of, setOf] = useState('')
  const [note, setNote] = useState('')
  const [asking, setAsking] = useState(false)
  const target = of.replace(/^\s*#/, '').trim()
  const valid = /^\d+$/.test(target) && Number(target) !== matterRef

  return (
    <Section title="Filed twice by mistake?">
      <p className="text-xs text-muted-foreground">
        If this is a second filing of a matter already on the board, withdraw it here. Nothing is
        deleted: it stays on record pointing at the original, keeps its documents, and leaves the
        board and the calendar.
      </p>
      <div className="mt-2 grid gap-2 sm:grid-cols-3">
        <Input
          className="h-8 text-xs"
          inputMode="numeric"
          value={of}
          onChange={(e) => setOf(e.target.value)}
          placeholder="It duplicates matter #"
          aria-label="Number of the matter this one duplicates"
        />
        <Input
          className="h-8 text-xs sm:col-span-2"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note, for example the same set-down uploaded twice"
        />
      </div>
      {of.trim() !== '' && !valid && (
        <p className="mt-1 text-[11px] text-muted-foreground">
          {Number(target) === matterRef
            ? 'That is this matter. Give the number of the other one.'
            : 'Type the number of the matter on the board, for example 1.'}
        </p>
      )}
      <Button
        className="mt-2"
        size="sm"
        variant="outline"
        disabled={busy !== null || !valid}
        onClick={() => setAsking(true)}
      >
        Withdraw as a duplicate
      </Button>
      <AlertDialog open={asking} onOpenChange={setAsking}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Withdraw matter #{matterRef} as a duplicate of #{target}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Matter #{matterRef} leaves the board and the calendar, and its open tasks are
              cancelled. It is not deleted: it stays on record as a second filing of #{target}, with
              its documents, and is listed at the foot of the board. The dispute owner is told by
              email.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it on the board</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                void run(
                  'withdraw-dup',
                  () =>
                    withdraw.trigger(
                      { id: matterId, duplicate_of: Number(target), note: note.trim() || null },
                      { skipCache: true },
                    ),
                  `Matter #${matterRef} withdrawn as a duplicate of #${target}.`,
                )
              }
            >
              Withdraw it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Section>
  )
}
